import React, { useState } from 'react';
import CollapsibleSection from './CollapsibleSection';

// Icons
const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const WarningIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const ShipIcon = () => (
  <svg className="section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M2 21c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1 .6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1" />
    <path d="M19.38 20A11.6 11.6 0 0 0 21 14l-9-4-9 4c0 2.9.94 5.34 2.81 7.76" />
    <path d="M19 13V7a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v6" />
    <path d="M12 10v4" />
  </svg>
);

const ChevronIcon = () => (
  <svg className="expand-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const ExternalLinkIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

const UrgentBadge = () => (
  <span className="urgent-badge" title="Arriving in the next 7 days">7️⃣</span>
);

// Utility constants
const CONTAINER_STATUS_LABELS: Record<string, string> = {
  'PICKUP': 'Picked Up',
  'GATEIN': 'Gate In',
  'LOAD': 'Loaded',
  'DEPART': 'Departed',
  'ARRIVE': 'Arrived',
  'UNLOAD': 'Unloaded',
  'GATEOUT': 'Gate Out',
  'DELIVER': 'Delivered',
  'RETURN': 'Returned',
  'TS_BERTHING': 'At Transhipment Port',
  'POD_BERTHING': 'At Destination Port',
  'IN_TRANSIT': 'In Transit',
  'UNCLASSIFIED': 'Unknown',
};

const TIMEZONE_LABELS: Record<string, string> = {
  '+09:00': 'Japan',
  '+08:00': 'China',
  '+07:00': 'Vietnam',
  '+05:30': 'India',
  '+01:00': 'CET',
  '+00:00': 'UTC',
  '-05:00': 'Eastern',
  '-06:00': 'Central',
  '-07:00': 'Mountain',
  '-08:00': 'Pacific',
};

// Utility functions
const getRelativeTime = (dateStr: string): string => {
  const eventDate = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - eventDate.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMs < 0) {
    return '';
  }
  if (diffHours < 1) {
    return 'just now';
  }
  if (diffHours === 1) {
    return '1 hour ago';
  }
  if (diffHours < 24) {
    return `${diffHours} hours ago`;
  }
  if (diffDays === 1) {
    return 'yesterday';
  }
  return `${diffDays} days ago`;
};

const formatEventTime = (dateStr: string, timezone: string | null): string => {
  const eventDate = new Date(dateStr);
  
  if (timezone) {
    const offsetMatch = timezone.match(/([+-])(\d{2}):(\d{2})/);
    if (offsetMatch && offsetMatch[1] && offsetMatch[2] && offsetMatch[3]) {
      const sign = offsetMatch[1] === '+' ? 1 : -1;
      const hours = parseInt(offsetMatch[2], 10);
      const minutes = parseInt(offsetMatch[3], 10);
      const offsetMs = sign * (hours * 60 + minutes) * 60 * 1000;
      const utcMs = eventDate.getTime();
      const localMs = utcMs + offsetMs + eventDate.getTimezoneOffset() * 60 * 1000;
      const localDate = new Date(localMs);
      
      const day = localDate.getDate().toString().padStart(2, '0');
      const month = localDate.toLocaleString('en-US', { month: 'short' });
      const hour = localDate.getHours().toString().padStart(2, '0');
      const minute = localDate.getMinutes().toString().padStart(2, '0');
      const tzLabel = TIMEZONE_LABELS[timezone] || timezone;
      
      return `${day} ${month} ${hour}:${minute} ${tzLabel}`;
    }
  }
  
  const day = eventDate.getDate().toString().padStart(2, '0');
  const month = eventDate.toLocaleString('en-US', { month: 'short' });
  const hour = eventDate.getHours().toString().padStart(2, '0');
  const minute = eventDate.getMinutes().toString().padStart(2, '0');
  return `${day} ${month} ${hour}:${minute}`;
};

const formatContainerStatus = (
  status: string,
  statusDate?: string | null,
  statusTimezone?: string | null
) => {
  if (!status || status === 'N/A') return null;
  
  const statusLabel = CONTAINER_STATUS_LABELS[status] || status;
  
  if (!statusDate) {
    return <span className="container-status-text">{statusLabel}</span>;
  }

  const relativeTime = getRelativeTime(statusDate);
  const absoluteTime = formatEventTime(statusDate, statusTimezone || null);
  
  if (!relativeTime) {
    return (
      <span className="container-status-text">
        {statusLabel} ({absoluteTime})
      </span>
    );
  }

  return (
    <span className="container-status-text">
      {statusLabel} {relativeTime} ({absoluteTime})
    </span>
  );
};

const getTrackingStatusPill = (trackingStatus: string) => {
  switch (trackingStatus) {
    case 'ON_TRACK':
      return (
        <span className="status-pill on-track">
          <CheckIcon /> On Track
        </span>
      );
    case 'DELAYED':
      return (
        <span className="status-pill delayed">
          <WarningIcon /> Delayed
        </span>
      );
    case 'AT_RISK':
      return (
        <span className="status-pill at-risk">
          <WarningIcon /> At Risk
        </span>
      );
    case 'N_A':
    default:
      return <span className="status-pill">N/A</span>;
  }
};

// Container detail within a shipment
export interface ContainerDetail {
  id: string;
  containerNumber: string | null;
  status: string;
}

// Shipment detail in vessel group
export interface ShipmentDetail {
  id: string;
  reference: string;
  contractRef: string;
  supplierCode: string;
  origin: string;
  destination: string;
  via: string | null;
  etd: string;
  eta: string;
  etdRaw: string | null;
  etaRaw: string | null;
  progress: number;
  status: string;
  containerCount: number;
  containers?: ContainerDetail[];
  portcastBookmarkId?: string | null;
  carrierCode?: string | null;
  buyer: string;
  buyerCode: string;
  trackingStatus: string;
  containerStatus?: string | null;
  containerStatusDate?: string | null;
  containerStatusTimezone?: string | null;
  daysUntilArrival?: number | null;
}

export interface VesselTracking {
  vesselVoy: string;
  containers: number;
  etd: string;
  eta: string;
  voyageTime: number;
  elapsed: number;
  progress: number;
  destination: string;
  shipmentCount: number;
  portcastBookmarkId?: string | null;
  carrier?: string;
  carrierCode?: string;
  shipments?: ShipmentDetail[];
}

interface ShipmentsEnRouteSectionProps {
  vessels: VesselTracking[];
}

// Tracking progress visualization component
const TrackingProgress: React.FC<{
  origin: string;
  destination: string;
  via?: string | null;
  progress: number;
}> = ({ origin, destination, via, progress }) => {
  // Calculate via point position (if exists, put at midpoint)
  const viaPosition = via ? 50 : null;

  return (
    <div className="tracking-progress">
      <div className="tracking-bar">
        <div className="tracking-line-bg" />
        <div className="tracking-line-fill" style={{ width: `${progress}%` }} />
        <span className="tracking-dot start" />
        {via && (
          <span
            className={`tracking-dot via${progress > 50 ? ' completed' : ''}`}
            style={{ left: `${viaPosition}%`, transform: 'translate(-50%, -50%)' }}
          />
        )}
        <span className="tracking-dot end" />
        <span className="tracking-ship" style={{ left: `${progress}%` }}>🚢</span>
      </div>
      <div className="tracking-places">
        <div className="tracking-place start">
          <span className="place-label">Origin</span>
          <span className="place-location">{origin || 'TBD'}</span>
        </div>
        {via && (
          <div className="tracking-place via">
            <span className="place-label">Via</span>
            <span className="place-location">{via}</span>
          </div>
        )}
        <div className="tracking-place end">
          <span className="place-label">Destination</span>
          <span className="place-location">{destination || 'TBD'}</span>
        </div>
      </div>
    </div>
  );
};

// Status badge component
const StatusBadge: React.FC<{ status: string; trackingUrl?: string | null }> = ({ status, trackingUrl }) => {
  const statusMap: Record<string, { label: string; className: string }> = {
    'IN_TRANSIT': { label: 'In Transit', className: 'in-transit' },
    'LOADED': { label: 'Loaded', className: 'load' },
    'ARRIVED': { label: 'Arrived', className: 'arrive' },
    'DISCHARGED': { label: 'Discharged', className: 'unload' },
    'GATE_OUT': { label: 'Gate Out', className: 'arrive' },
    'GATE_IN': { label: 'Gate In', className: 'load' },
    'AT_TRANSSHIP': { label: 'At Transship', className: 'berthing' },
  };

  const { label, className } = statusMap[status] || { label: status, className: 'in-transit' };

  if (trackingUrl) {
    return (
      <a
        href={trackingUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`event-badge ${className}`}
      >
        {label} <ExternalLinkIcon />
      </a>
    );
  }

  return <span className={`event-badge ${className}`}>{label}</span>;
};

// Carrier SCAC to short name mapping
const carrierShortNames: Record<string, string> = {
  'ONEY': 'ONE',
  'NYKS': 'NYK',
  'MSCU': 'MSC',
  'MAEU': 'Maersk',
  'EGLV': 'Evergreen',
  'COSU': 'COSCO',
  'HLCU': 'Hapag',
  'YMLU': 'YM',
  'CMDU': 'CMA CGM',
  'OOLU': 'OOCL',
  'HDMU': 'HMM',
  'ZIMU': 'ZIM',
  'ANNU': 'ANL',
  'APLU': 'APL',
};

// Carrier badge component
const CarrierBadge: React.FC<{ carrierCode: string; carrierName: string }> = ({ carrierCode, carrierName }) => {
  if (!carrierCode) {
    return <span className="carrier-badge unknown">N/A</span>;
  }
  const scac = carrierCode.toUpperCase();
  const shortName = carrierShortNames[scac] || carrierCode;
  const cssClass = scac.toLowerCase();
  return (
    <span className={`carrier-badge ${cssClass}`} title={carrierName || scac}>
      {shortName}
    </span>
  );
};

// Date tag component
const DateTag: React.FC<{ isActual: boolean }> = ({ isActual }) => (
  <span className={`date-tag ${isActual ? 'actual' : 'estimated'}`}>
    {isActual ? 'Actual' : 'Est'}
  </span>
);

const getTrackingUrl = (
  carrierCode: string | null | undefined,
  containerNumber: string | null | undefined,
  portcastBookmarkId: string | null | undefined
): string | null => {
  if (carrierCode?.toUpperCase() === 'ONEY' && containerNumber) {
    return `https://ecomm.one-line.com/one-ecom/manage-shipment/cargo-tracking?trakNoParam=${containerNumber}`;
  }
  if (portcastBookmarkId) {
    return `https://app.portcast.io/v2/container_tracking/${portcastBookmarkId}`;
  }
  return null;
};

const ShipmentRow: React.FC<{
  shipment: ShipmentDetail;
  vesselVoy: string;
  carrier: string;
  carrierCode: string;
}> = ({ shipment, vesselVoy, carrier, carrierCode }) => {
  const [expanded, setExpanded] = useState(false);

  const now = new Date();
  const etdDate = shipment.etdRaw ? new Date(shipment.etdRaw) : null;
  const etaDate = shipment.etaRaw ? new Date(shipment.etaRaw) : null;
  const etdIsActual = etdDate ? etdDate < now : false;
  const etaIsActual = etaDate ? etaDate < now : false;

  const containers = shipment.containers || [];
  const effectiveCarrierCode = shipment.carrierCode || carrierCode;

  return (
    <>
      <tr
        className={`expandable-row${expanded ? ' expanded' : ''}`}
        onClick={() => setExpanded(!expanded)}
      >
        <td>
          <div className="row-main">
            <ChevronIcon />
            <a
              href={`/contracts/${shipment.contractRef}`}
              className="ref-link"
              onClick={(e) => e.stopPropagation()}
            >
              {shipment.reference}
            </a>
            {shipment.daysUntilArrival !== undefined && shipment.daysUntilArrival !== null && shipment.daysUntilArrival <= 7 && <UrgentBadge />}
            {shipment.containerCount > 1 && (
              <span className="container-count">({shipment.containerCount})</span>
            )}
          </div>
        </td>
        <td>
          {getTrackingStatusPill(shipment.trackingStatus)}
        </td>
        <td>
          {shipment.etd} {shipment.etdRaw && <DateTag isActual={etdIsActual} />}
        </td>
        <td>
          <TrackingProgress
            origin={shipment.origin}
            destination={shipment.destination}
            via={shipment.via}
            progress={shipment.progress}
          />
        </td>
        <td>
          {shipment.eta} {shipment.etaRaw && <DateTag isActual={etaIsActual} />}
        </td>
        <td className="hidden-xs hidden-sm">
          {shipment.buyer}
        </td>
      </tr>
      <tr className={`detail-row${expanded ? ' visible' : ''}`}>
        <td></td>
        <td colSpan={5}>
          {containers.map((container, idx) => {
            const trackingUrl = getTrackingUrl(
              effectiveCarrierCode,
              container.containerNumber,
              shipment.portcastBookmarkId
            );
              return (
                <div key={container.id} className="detail-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', ...(idx > 0 ? { marginTop: '8px', paddingTop: '8px', borderTop: '1px solid var(--border-light)' } : {}) }}>
                  <div className="detail-item">
                    <span className="detail-label">Carrier</span>
                    <span className="detail-value">
                      <CarrierBadge carrierCode={effectiveCarrierCode || ''} carrierName={carrier} />
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Container</span>
                    <span className="detail-value">
                      {trackingUrl ? (
                        <a
                          href={trackingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="container-link"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {container.containerNumber || 'TBD'} <ExternalLinkIcon />
                        </a>
                      ) : (
                        container.containerNumber || 'TBD'
                      )}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Vessel / Voyage</span>
                    <span className="detail-value">{vesselVoy}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Container Status</span>
                    <span className="detail-value">
                      {formatContainerStatus(
                        shipment.containerStatus || container.status,
                        shipment.containerStatusDate,
                        shipment.containerStatusTimezone
                      )}
                    </span>
                  </div>
                </div>
              );
            })}
            {containers.length === 0 && (
              <div className="detail-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
                <div className="detail-item">
                  <span className="detail-label">Carrier</span>
                  <span className="detail-value">
                    <CarrierBadge carrierCode={effectiveCarrierCode || ''} carrierName={carrier} />
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Container</span>
                  <span className="detail-value">TBD</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Vessel / Voyage</span>
                  <span className="detail-value">{vesselVoy}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Container Status</span>
                  <span className="detail-value">
                    {formatContainerStatus(
                      shipment.containerStatus || 'N/A',
                      shipment.containerStatusDate,
                      shipment.containerStatusTimezone
                    )}
                  </span>
                </div>
              </div>
            )}
        </td>
      </tr>
    </>
  );
};

const ShipmentsEnRouteSection: React.FC<ShipmentsEnRouteSectionProps> = ({
  vessels,
}) => {
  const totalContainers = vessels.reduce((sum, v) => sum + v.containers, 0);
  const totalShipments = vessels.reduce((sum, v) => sum + (v.shipments?.length || v.shipmentCount), 0);

  if (vessels.length === 0) {
    return (
      <CollapsibleSection
        title="Shipments en Route"
        count={0}
        icon={<ShipIcon />}
      >
        <div className="empty-state">
          <div className="empty-state-icon">🚢</div>
          <div className="empty-state-text">No shipments currently at sea</div>
        </div>
      </CollapsibleSection>
    );
  }

  const hasShipmentDetails = vessels.some(v => v.shipments && v.shipments.length > 0);

  if (!hasShipmentDetails) {
    return (
      <CollapsibleSection
        title="Shipments en Route"
        count={vessels.length}
        icon={<ShipIcon />}
      >
        <div className="table-wrapper">
          <table className="dash-table">
            <thead>
              <tr>
                <th>Vessel/Voy.</th>
                <th>Containers</th>
                <th className="hidden-xs hidden-sm">ETD</th>
                <th>ETA</th>
                <th>Progress</th>
                <th className="hidden-xs hidden-sm">Destination</th>
              </tr>
            </thead>
            <tbody>
              {vessels.map((vessel) => {
                const vesselLink = `/ship/${vessel.vesselVoy.replace(/ /g, '_')}`;
                const trackingUrl = vessel.portcastBookmarkId
                  ? `https://app.portcast.io/v2/container_tracking/${vessel.portcastBookmarkId}`
                  : null;

                return (
                  <tr key={vessel.vesselVoy} className="vessel-row">
                    <td>
                      <strong>
                        <a href={vesselLink} className="ref-link">
                          {vessel.vesselVoy}
                        </a>
                      </strong>
                    </td>
                    <td className="text-center">{vessel.containers}</td>
                    <td className="text hidden-xs hidden-sm">{vessel.etd}</td>
                    <td className="text">{vessel.eta}</td>
                    <td>
                      <div className="progress-bar-container">
                        <div
                          className="progress-bar-fill"
                          style={{ width: `${vessel.progress}%` }}
                          role="progressbar"
                          aria-valuenow={vessel.progress}
                          aria-valuemin={0}
                          aria-valuemax={100}
                        />
                        <span className="progress-label">{vessel.progress}%</span>
                      </div>
                    </td>
                    <td className="text hidden-xs hidden-sm">
                      {vessel.destination}
                      {trackingUrl && (
                        <a
                          href={trackingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="tracking-link"
                          title="View tracking"
                        >
                          <ExternalLinkIcon />
                        </a>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="section-footer">
          <span>{totalContainers} containers on {vessels.length} vessel{vessels.length !== 1 ? 's' : ''} currently at sea</span>
        </div>
      </CollapsibleSection>
    );
  }

  return (
    <CollapsibleSection
      title="Shipments en Route"
      count={totalShipments}
      icon={<ShipIcon />}
    >
      <div className="table-wrapper vessels-expandable">
        <table className="dash-table expandable-table">
          <thead>
            <tr>
              <th style={{ width: '140px' }}>Reference</th>
              <th style={{ width: '110px' }}>Tracking</th>
              <th style={{ width: '100px' }}>Departure</th>
              <th>Progress</th>
              <th style={{ width: '100px' }}>Arrival</th>
              <th style={{ width: '120px' }} className="hidden-xs hidden-sm">Buyer</th>
            </tr>
          </thead>
          <tbody>
            {vessels.flatMap((vessel) =>
              vessel.shipments?.map((shipment) => (
                <ShipmentRow
                  key={shipment.id}
                  shipment={shipment}
                  vesselVoy={vessel.vesselVoy}
                  carrier={vessel.carrier || ''}
                  carrierCode={vessel.carrierCode || ''}
                />
              )) || []
            )}
          </tbody>
        </table>
      </div>

      <div className="section-footer">
        <span>{totalContainers} containers on {vessels.length} vessel{vessels.length !== 1 ? 's' : ''} currently at sea</span>
      </div>
    </CollapsibleSection>
  );
};

export default ShipmentsEnRouteSection;
